import type { Transition } from "@uirouter/core";
import type { transitionHookFn } from "app/routes/handlers/redirectFunctions/redirectPipe";
import { SwRedux } from "app/services/SwRedux";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";
import { getDurationRequiredChanges } from "../../handlers/duration/durationFilterConsistencyHandler";
import { getCountryRequiredChanges } from "../../handlers/country/countryFilterConsistencyHandler";
import { getWebSourceIndustryRequiredChanges } from "../../handlers/webSource/webSourceFilterConsistencyIndustryHandler";
import {
    shouldApplyFilterConsistency,
    saveFilterConsistencyAfterFirstNavigation,
    testAndApplyChanges,
    EUrlKeys,
} from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { isEmpty } from "lodash";
export const onRedirectSegmentAnalysis = (): transitionHookFn => {
    return (transition: Transition) => {
        const fromState = transition.from() as IRouterState;
        const toParams = transition.params("to");
        if (!shouldApplyFilterConsistency(fromState, toParams, EUrlKeys.SEGMENT)) {
            return;
        }
        const swRedux = SwRedux.getInstance();
        const targetState = transition.to() as IRouterState;
        const {
            digitalSuiteSelectedCountry,
            digitalSuiteSelectedDuration,
            digitalSuiteSelectedWebSource,
            isNewSession,
        } = swRedux.getState().usersDigitalSuiteFiltersSelection;
        if (!isNewSession) {
            const componentConfigId = proNavigator.getConfigIdFromStateConfig(targetState, toParams);

            const durationChanges = getDurationRequiredChanges(
                componentConfigId,
                digitalSuiteSelectedDuration,
                targetState,
                toParams,
            );

            const countryChanges = getCountryRequiredChanges(
                componentConfigId,
                digitalSuiteSelectedCountry,
                targetState,
                toParams,
            );

            const webSourceChanges = getWebSourceIndustryRequiredChanges(
                durationChanges.isAllowed,
                digitalSuiteSelectedDuration,
                countryChanges.isAllowed,
                digitalSuiteSelectedCountry,
                digitalSuiteSelectedWebSource,
                targetState,
                toParams,
            );

            const changedParams = testAndApplyChanges(
                countryChanges,
                durationChanges,
                webSourceChanges,
                digitalSuiteSelectedCountry,
                digitalSuiteSelectedDuration,
                digitalSuiteSelectedWebSource,
                toParams?.isDurationChanged,
                toParams?.isWebSourceChanged,
            );
            if (
                !durationChanges.isAllowed &&
                durationChanges.displayChangeIndicator &&
                durationChanges.isChanged &&
                toParams.duration === digitalSuiteSelectedDuration
            ) {
                //change to default duration
                changedParams.duration = targetState.defaultQueryParams.duration;
            }
            if (!isEmpty(changedParams)) {
                return { params: { ...toParams, ...changedParams } };
            }
        } else {
            // we want to save the filters after the first navigation as selected filters
            saveFilterConsistencyAfterFirstNavigation(targetState, toParams);
        }
    };
};
